import { useLayoutEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
const RESIZE_DEBOUNCE_TIMEOUT = 100;
/**
 * Hook to measure the width of the scrollbar of a given element.
 * @param ref - Ref object to the element to measure the scrollbar width of.
 * @param deps - Dependencies to trigger the measurement.
 * @returns The width of the scrollbar.
 */
export function useScrollbarWidth(ref, ...deps) {
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const updateScrollbarWidth = useCallbackRef(() => {
        const el = ref.current;
        if (!el)
            return;
        setScrollbarWidth(el.offsetWidth - el.clientWidth);
    });
    const updateScrollbarWidthDebounced = useDebouncedCallback(updateScrollbarWidth, RESIZE_DEBOUNCE_TIMEOUT);
    useLayoutEffect(() => {
        // Immediate measurement on mount/deps change
        updateScrollbarWidth();
    }, [deps, updateScrollbarWidth]);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element)
            return undefined;
        // Use ResizeObserver for better performance if available
        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(updateScrollbarWidthDebounced);
            resizeObserver.observe(element);
            return () => {
                resizeObserver.disconnect();
            };
        }
        // Fallback to window resize event
        window.addEventListener('resize', updateScrollbarWidthDebounced);
        return () => {
            window.removeEventListener('resize', updateScrollbarWidthDebounced);
        };
    }, [updateScrollbarWidthDebounced, ref]);
    return scrollbarWidth;
}
